import { useState } from 'react';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { CopyButton } from '@components/copy-button';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { InfoBox } from '@components/info-box';
import { JsonObject } from '@components/json-editor';
import { JsonTypeTree } from '@components/json-type-tree';
import { RelativeSpinner } from '@components/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';

import { useWebSocket } from 'api/hooks';
import { useUpdateJourneyWebhook } from 'api/journey-webhooks/hooks';
import { JourneyWebhook } from 'api/journey-webhooks/requests';

export const useWebhookAutoConfig = () => useModal(WebhookAutoConfig, { variant: 'LARGE' });

type AutoConfigMessage = {
  content: {
    payload: JsonObject;
    files: JsonObject;
    headers: JsonObject;
  };
  type: string;
};

export const WebhookAutoConfig = ({ webhook }: { webhook: JourneyWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const [isLoading, setIsLoading] = useState(true);
  const [output, setOutput] = useState<AutoConfigMessage['content']>();
  const updateMutation = useUpdateJourneyWebhook(webhook);

  const handleSubmit = () => {
    updateMutation.mutate(
      {
        payloadVariables: JSON.stringify(output?.payload),
        headerVariables: JSON.stringify(output?.headers),
        fileVariables: JSON.stringify(output?.files),
      },
      {
        onSuccess: hideModal,
      },
    );
  };

  useWebSocket(`webhook/auto-config/${webhook.shortId}/`, (data: MessageEvent<string>) => {
    const message = JSON.parse(data.data) as AutoConfigMessage;
    setOutput(message.content);
    setIsLoading(false);
  });

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={6}>
          {isLoading && (
            <>
              <Stack space={4}>
                <Text font="bold14" text={t('step1')} />
                <Stack orientation="horizontal" align="flex-end" space={2}>
                  <div style={{ flex: 1 }}>
                    <TextField
                      label=""
                      hideLabel
                      defaultValue={webhook.triggerUrl}
                      isDisabled
                      readOnly
                    />
                  </div>
                  <CopyButton getText={() => webhook.triggerUrl} size="md" />
                </Stack>
              </Stack>
              <Stack space={4}>
                <Stack>
                  <Text font="bold14" text={t('step2')} />
                  <Text font="regular14" color="textSecondary" text={t('step2Description')} />
                </Stack>
                <Stack space={6} justify="center">
                  <RelativeSpinner />
                  <Stack>
                    <Text font="bold16" text={t('waiting')} center />
                    <Text font="regular14" color="textSecondary" text={t('listening')} center />
                  </Stack>
                </Stack>
              </Stack>
            </>
          )}
          {!isLoading && output && (
            <>
              <Stack space={4}>
                <Stack orientation="horizontal" align="flex-end" space={2}>
                  <div style={{ flex: 1 }}>
                    <TextField
                      label=""
                      hideLabel
                      defaultValue={webhook.triggerUrl}
                      isDisabled
                      readOnly
                    />
                  </div>
                  <CopyButton getText={() => webhook.triggerUrl} size="md" iconOnly />
                </Stack>
                <InfoBox
                  variant="green"
                  icon={<IconFormIndicatorSuccess />}
                  text={t('successfullyConfigured')}
                />
              </Stack>
              <Separator />
              <JsonTypeTree
                data={output}
                showIconForTypes={['string', 'array', 'array-objects', 'array-strings', 'object']}
                unsupportedTypes={['array', 'array-objects', 'array-strings']}
              />
            </>
          )}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup align="right">
          <Button text={t('core.cancel')} variant="secondary" onClick={hideModal} />
          <Button
            text={t('addPayload')}
            variant="primary-blue"
            isDisabled={isLoading}
            onClick={handleSubmit}
          />
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Configure trigger automatically',
    step1: '1. Copy the link',
    step2: '2. Make an HTTP POST request to the endpoint',
    step2Description:
      'To configure this trigger automatically, make an HTTP POST request to the trigger URL from the system that will be triggering the workflow\n\nAny parameters should be included in the JSON body',
    waiting: 'Waiting...',
    listening: 'Listening to incoming requests',
    successfullyConfigured: 'Successfully configured trigger',
    payload: 'Payload',
    headers: 'Headers',
    addPayload: 'Add payload',
  },
};
